import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useAssetContext } from '@/context/AssetContext';
import { fetchWithRetry } from '@/utils/fetchWithRetry';
import { useSections } from '@/hooks/useSections';
import { 
  Facebook, 
  Instagram, 
  Linkedin, 
  Youtube, 
  Twitter,
  Phone, 
  Mail, 
  MapPin,
  ArrowRight
} from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = window.location.origin;

// Social icon mapping
const socialIcons = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
  youtube: Youtube,
  twitter: Twitter
};

// Default fallback links (used if CMS data not available)
const defaultDestinations = [
  { name: 'USA', href: '/destinations/usa' },
  { name: 'UK', href: '/destinations/uk' },
  { name: 'Canada', href: '/destinations/canada' },
  { name: 'Australia', href: '/destinations/australia' },
  { name: 'Germany', href: '/destinations/germany' },
  { name: 'New Zealand', href: '/destinations/new-zealand' },
];

const defaultServices = [
  { name: 'Profile Assessment', href: '/services' },
  { name: 'University Shortlisting', href: '/services' },
  { name: 'Application Support', href: '/services' },
  { name: 'Visa Assistance', href: '/services' },
  { name: 'Education Loan', href: '/services' },
  { name: 'Test Preparation', href: '/services' },
];

const defaultCompany = [
  { name: 'About Us', href: '/about' },
  { name: 'Success Stories', href: '/success-stories' },
  { name: 'Events', href: '/events' },
  { name: 'Blog', href: '/blog' },
  { name: 'Our Offices', href: '/locations' },
  { name: 'Contact Us', href: '/contact' },
];

const defaultPrograms = [
  { name: 'MS in Computer Science - USA', href: '/programs/ms-in-computer-science-usa' },
  { name: 'MBA in Canada', href: '/programs/mba-in-canada' },
  { name: 'MS in Data Science - UK', href: '/programs/ms-in-data-science-uk' },
  { name: 'MBBS Abroad', href: '/programs/mbbs-abroad' },
  { name: 'Engineering in Germany', href: '/programs/engineering-in-germany' },
  { name: 'View All Programs', href: '/programs' },
];

const defaultComparisons = [
  // Batch 3 revert of Batch-1 stopgap: the /compare/{slug} detail pages
  // DO exist with full content — Batch-1 mis-tested with shorter slugs.
  // Real slugs verified in `db.comparisons` and via
  // GET /api/comparisons/public/{slug} → 200 for each of these four.
  { name: 'MS: USA vs UK vs Canada', href: '/compare/ms-in-usa-vs-uk-vs-canada' },
  { name: 'MBA vs MS', href: '/compare/mba-vs-ms-which-is-better' },
  { name: 'Canada vs Australia', href: '/compare/study-in-canada-vs-australia' },
  { name: 'USA vs Germany', href: '/compare/ms-usa-vs-masters-germany' },
  { name: 'View All Comparisons', href: '/compare' },
];

export const Footer = () => {
  const { isDark } = useTheme();
  const { getLogo } = useAssetContext();
  const { pathname } = useLocation();
  // Pages that already render their own final CTA/"Ready to Start Your Journey?"
  // section — suppress the Footer's universal CTA strip on these routes to
  // avoid a visual duplicate. Add new routes here as pages ship their own CTA.
  const CTA_SELF_SERVED_ROUTES = [
    '/',
    '/about',
    '/success-stories',
    '/contact',
    '/compare', // list page — has "Need Personalized Advice?" card
    '/programs', // list page — has final_cta section
    '/services', // list page — has "Need Help Choosing?" CTA
  ];
  const CTA_SELF_SERVED_PREFIXES = [
    '/compare/',       // detail — has "Talk to AI Counselor" CTA
    '/destinations/',  // detail — has "Ready to Study in {country}?" CTA
  ];
  const hasSelfServedPrefix = CTA_SELF_SERVED_PREFIXES.some((p) => pathname.startsWith(p));
  const showTopCta = !CTA_SELF_SERVED_ROUTES.includes(pathname) && !hasSelfServedPrefix;
  
  // CMS Footer Settings State
  const [footerSettings, setFooterSettings] = useState({
    company_name: 'UniXperts',
    tagline: 'Your Gateway to Global Education',
    contact_email: 'info@unixperts.com',
    contact_phone: '+91 96395 57799',
    whatsapp_number: '+919639557799',
    address: 'Gurugram, India',
    social_links: [
      { platform: 'facebook', url: 'https://facebook.com/unixperts', is_active: true },
      { platform: 'instagram', url: 'https://instagram.com/unixperts', is_active: true },
      { platform: 'linkedin', url: 'https://www.linkedin.com/company/unixperts/about/', is_active: true },
      { platform: 'youtube', url: 'https://youtube.com/@unixperts', is_active: true }
    ],
    copyright_text: null,
    show_newsletter: true,
    // Navigation links - will be populated from CMS
    destinations_links: defaultDestinations,
    services_links: defaultServices,
    company_links: defaultCompany,
    programs_links: defaultPrograms,
    comparisons_links: defaultComparisons
  });
  
  // CMS Global Settings (cta_labels for the footer CTA strip)
  const [ctaLabels, setCtaLabels] = useState({});

  // Section-based CMS (Phase B Step 4). If section records exist they win over
  // the legacy `footer_settings` doc, but we still merge legacy for graceful
  // fallback while other pages migrate.
  const { getSection: getFooterSection } = useSections('footer');
  const brandSec = getFooterSection('brand')?.content || {};
  const contactSec = getFooterSection('contact')?.content || {};
  const socialSec = getFooterSection('social')?.content || {};
  const legalSec = getFooterSection('legal')?.content || {};

  const displayName = brandSec.company_name || footerSettings.company_name;
  const displayTagline = brandSec.tagline || footerSettings.tagline;
  const displayPhone = contactSec.phone || footerSettings.contact_phone;
  const displayPhoneHref = contactSec.phone_href || `tel:${(footerSettings.contact_phone || '').replace(/\s/g, '')}`;
  const displayEmail = contactSec.email || footerSettings.contact_email;
  const displayEmailHref = contactSec.email_href || `mailto:${footerSettings.contact_email || ''}`;
  const displayWhatsappHref = contactSec.whatsapp_href || `https://wa.me/${(footerSettings.whatsapp_number || '').replace(/\D/g, '')}`;
  const displaySocial = Array.isArray(socialSec.items) && socialSec.items.length > 0
    ? socialSec.items.map((s) => ({ platform: s.platform, url: s.href, is_active: s.is_active !== false }))
    : footerSettings.social_links;
  // Footer copyright uses the legal entity name required for compliance,
  // not the marketing brand. Falls back to CMS overrides if authored.
  const displayCopyright = legalSec.copyright || footerSettings.copyright_text || `© ${new Date().getFullYear()} UNI ACADEMIC CONSULTANTS LLP. All rights reserved.`;
  // Always ensure Data Disclaimer, Privacy Policy and Terms of Service links
  // are present in the Legal section — even when CMS returns a custom
  // `legalSec.links` array without them.
  const cmsLegalLinks = Array.isArray(legalSec.links) ? legalSec.links : null;
  const REQUIRED_LEGAL_LINKS = [
    { label: 'Privacy Policy',  href: '/privacy_policy' },
    { label: 'Data Disclaimer', href: '/disclaimer' },
    { label: 'Terms of Service', href: '/terms' },
  ];
  const displayLegalLinks = (() => {
    if (!cmsLegalLinks) return null; // fall through to the hardcoded fallback below
    // Drop legacy refund policy links and de-dupe by href, then append any
    // required legal link that's missing.
    const filtered = cmsLegalLinks
      .filter((l) => !/refund/i.test(l.label || '') && !/refund/i.test(l.href || ''));
    const seen = new Set(filtered.map((l) => (l.href || '').toLowerCase()));
    REQUIRED_LEGAL_LINKS.forEach((req) => {
      if (!seen.has(req.href.toLowerCase())) filtered.push(req);
    });
    return filtered;
  })();
  
  // Fetch footer + global settings from CMS in parallel
  useEffect(() => {
    const fetchSettings = async () => {
      const [footerRes, globalRes] = await Promise.all([
        fetchWithRetry(`${API_URL}/api/cms/site-settings/footer`, {}, { label: 'footer-settings' }),
        fetchWithRetry(`${API_URL}/api/cms/pages/public/global-settings`, {}, { label: 'global-settings' })
      ]);
      if (footerRes && footerRes.ok) {
        const data = await footerRes.json();
        setFooterSettings(prev => ({ ...prev, ...data }));
      }
      if (globalRes && globalRes.ok) {
        const data = await globalRes.json();
        if (data.content && data.content.cta_labels) {
          setCtaLabels(data.content.cta_labels);
        }
      }
    };
    fetchSettings();
  }, []);
  
  // Get active links from CMS or fallback to defaults
  const destinations = (footerSettings.destinations_links || defaultDestinations).filter(l => l.is_active !== false);
  const services = (footerSettings.services_links || defaultServices).filter(l => l.is_active !== false);
  const company = (footerSettings.company_links || defaultCompany).filter(l => l.is_active !== false);
  const popularPrograms = (footerSettings.programs_links || defaultPrograms).filter(l => l.is_active !== false);
  const comparisons = (footerSettings.comparisons_links || defaultComparisons).filter(l => l.is_active !== false);
  
  return (
    <footer className={`${isDark ? 'bg-[#1a2830]' : 'bg-ux-charcoal'} text-white`} data-testid="main-footer">
      {/* CTA Section — hidden on Homepage (homepage#final_cta section already renders one) */}
      {showTopCta && (
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-ux-blue via-ux-violet to-ux-cyan" />
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <h3 className="text-2xl md:text-4xl font-bold text-white mb-2" data-testid="footer-cta-title">
                {ctaLabels.footer_cta_title || 'Ready to Start Your Journey?'}
              </h3>
              <p className="text-white/80 text-lg" data-testid="footer-cta-subtitle">
                {ctaLabels.footer_cta_subtitle || 'Join 30,000+ students who found their dream university with UniXperts'}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                className="bg-white text-ux-blue hover:bg-white/90 font-bold rounded-full px-8"
                asChild
              >
                <Link to="/contact" data-testid="footer-cta-button">
                  {ctaLabels.footer_cta_button || ctaLabels.book_consultation || 'Book Free Consultation'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Main Footer */}
      <div className={`${isDark ? 'bg-[#233842]' : 'bg-ux-charcoal'} py-16`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-8 lg:gap-6">
            {/* Logo & About */}
            <div className="col-span-2 md:col-span-3 lg:col-span-1">
              <Link to="/" className="inline-block mb-6">
                <img src={getLogo()} alt="UniXperts" className="h-10 brightness-0 invert" />
              </Link>
              <p className="text-white/60 text-sm mb-6">
                {footerSettings.tagline || 'Your trusted partner in global education. 15+ years of helping students achieve their dreams.'}
              </p>
              {/* Social Icons from CMS */}
              <div className="flex gap-3">
                {(footerSettings.social_links || []).filter(s => s.is_active && s.url).map((social, i) => {
                  const IconComponent = socialIcons[social.platform] || Facebook;
                  return (
                    <a 
                      key={i}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-xl bg-white/5 hover:bg-ux-cyan/20 flex items-center justify-center transition-colors"
                    >
                      <IconComponent className="w-5 h-5 text-white/70 hover:text-ux-cyan" />
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Destinations */}
            <div>
              <h4 className="text-white font-bold mb-4">Destinations</h4>
              <ul className="space-y-2">
                {destinations.map((item) => (
                  <li key={item.name}>
                    <Link to={item.href} className="text-white/60 hover:text-ux-cyan text-sm transition-colors">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Popular Programs */}
            <div>
              <h4 className="text-white font-bold mb-4">Popular Programs</h4>
              <ul className="space-y-2">
                {popularPrograms.map((item) => (
                  <li key={item.name}>
                    <Link 
                      to={item.href} 
                      className={`text-sm transition-colors ${item.name === 'View All Programs' ? 'text-ux-cyan hover:text-ux-cyan/80 font-medium' : 'text-white/60 hover:text-ux-cyan'}`}
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-white font-bold mb-4">Services</h4>
              <ul className="space-y-2">
                {services.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className="text-white/60 hover:text-ux-cyan text-sm transition-colors"
                      data-testid={`footer-service-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Compare */}
            <div>
              <h4 className="text-white font-bold mb-4">Compare Options</h4>
              <ul className="space-y-2">
                {comparisons.map((item) => (
                  <li key={item.name}>
                    <Link 
                      to={item.href} 
                      className={`text-sm transition-colors ${item.name === 'View All Comparisons' ? 'text-ux-cyan hover:text-ux-cyan/80 font-medium' : 'text-white/60 hover:text-ux-cyan'}`}
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-white font-bold mb-4">Company</h4>
              <ul className="space-y-2">
                {company.map((item) => (
                  <li key={item.name}>
                    <Link to={item.href} className="text-white/60 hover:text-ux-cyan text-sm transition-colors">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Get Free Consultation CTA */}
            <div className="col-span-2 md:col-span-3 lg:col-span-1">
              <h4 className="text-white font-bold mb-4">Start Your Journey</h4>
              <p className="text-white/60 text-sm mb-4">
                Get expert guidance from our counselors. Free consultation for your study abroad plans.
              </p>
              <Button
                size="lg"
                className="w-full bg-gradient-to-r from-ux-cyan to-ux-blue text-white rounded-full h-auto px-5 py-3 text-xs sm:text-sm font-semibold hover:opacity-90 transition-opacity"
                asChild
              >
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 whitespace-normal leading-tight text-center">
                  <span>{ctaLabels.get_free_consultation || 'Get Free Consultation'}</span>
                  <ArrowRight className="w-4 h-4 flex-shrink-0" />
                </Link>
              </Button>
            </div>
          </div>

          {/* Contact Info Bar */}
          <div className={`mt-12 pt-8 border-t ${isDark ? 'border-white/10' : 'border-white/10'}`}>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <a href={displayPhoneHref} className="flex items-center gap-2 text-white/60 hover:text-ux-cyan">
                <Phone className="w-4 h-4" />
                {displayPhone}
              </a>
              <a href={displayEmailHref} className="flex items-center gap-2 text-white/60 hover:text-ux-cyan">
                <Mail className="w-4 h-4" />
                {displayEmail}
              </a>
              {footerSettings.address && (
                <span className="flex items-center gap-2 text-white/60">
                  <MapPin className="w-4 h-4" />
                  {footerSettings.address}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className={`${isDark ? 'bg-[#1a2830]' : 'bg-black/20'} py-4`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/50">
            <p>{displayCopyright}</p>
            <div className="flex gap-6">
              {displayLegalLinks
                ? displayLegalLinks.map((l, i) => (
                    <Link
                      key={`${l.href}-${i}`}
                      to={l.href}
                      className="hover:text-ux-cyan transition-colors"
                      data-testid={l.href === '/disclaimer' ? 'footer-disclaimer-link' : undefined}
                    >
                      {l.label}
                    </Link>
                  ))
                : (
                  <>
                    <Link to="/privacy_policy" className="hover:text-ux-cyan transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
                    <Link to="/terms" className="hover:text-ux-cyan transition-colors" data-testid="footer-terms-link">Terms of Service</Link>
                    <Link to="/disclaimer" className="hover:text-ux-cyan transition-colors" data-testid="footer-disclaimer-link">Data Disclaimer</Link>
                  </>
                )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
